import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useRouter } from "next/router";
import { useEffect, useState, VFC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink, Loader } from "components";
import { PortfoliosContent } from "types/graphql/generated";
import { Selector } from "./components/Selector";
import { Table } from "./components/Table";
import { usePortfolioTemplates } from "./usePortfolioTemplates";

interface Props {
  content: PortfoliosContent;
}

const Portfolios: VFC<Props> = withErrorBoundary(
  ({ content: { portfoliosDescription, fundMenuLabel, fundMenuLink, defaultSlug } }) => {
    const { query, replace } = useRouter();
    const { error, loading, portfolios = [] } = usePortfolioTemplates();
    const [activePortfolioIndex, setActivePortfolioIndex] = useState(0);
    const activePortfolio = portfolios[activePortfolioIndex];

    useEffect(() => {
      const portfolioIndexAccordingSlug = portfolios.findIndex((portfolio) => portfolio.slug === query.slug);

      if (portfolioIndexAccordingSlug !== -1) {
        setActivePortfolioIndex(portfolioIndexAccordingSlug);
      }

      if (portfolioIndexAccordingSlug === -1) {
        replace(`/portfolios/${defaultSlug ?? portfolios[0].slug}`);
      }
    }, [portfolios, query.slug]);

    if (loading) {
      return (
        <StyledPortfolios className="Content">
          <div className="Portfolios">
            <Loader className="Portfolios__loader" />
          </div>
        </StyledPortfolios>
      );
    }

    if (error || !portfolios || portfolios.length === 0) {
      return null;
    }

    return (
      <StyledPortfolios className="Content">
        <div className="Portfolios">
          <ul className="Portfolios__selectors">
            {portfolios.map((portfolio, index) => {
              if (!portfolio) {
                return null;
              }

              // Signify the last portfolio has rendered (useful for snapshots).
              const lastPortfolio = portfolios.length - 1 === index;
              const lastPortfolioClassName = lastPortfolio ? "Portfolio__last" : "";

              return (
                <Selector
                  portfolio={portfolio}
                  key={portfolio.name}
                  active={index === activePortfolioIndex}
                  className={`Portfolios__selector ${lastPortfolioClassName}`}
                />
              );
            })}
          </ul>
          <div className="Portfolios__tableContainer">
            <div className="Portfolios__tableContainerTextContent">
              <h2 className="Portfolios__title Font_32_40_600">
                {!!portfolios.length && portfolios[activePortfolioIndex].name}
              </h2>
              <h3 className="Portfolios__tableContainerTextTitle Font_24_32_450">
                {activePortfolio.distribution.stock.display} Stocks / {activePortfolio.distribution.bond.display} Bonds
              </h3>
              <Markdown className="Portfolios__tableContainerTextDescription Color_tertiary Font_12_16_450">
                {portfoliosDescription}
              </Markdown>
            </div>
            <Table className="Portfolios__table" portfolio={activePortfolio} />
          </div>
          <ArrowLink href={fundMenuLink} className="Portfolios__pageLink Font_18_26_600">
            {fundMenuLabel}
          </ArrowLink>
        </div>
      </StyledPortfolios>
    );
  },
  {}
);

const StyledPortfolios = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Portfolios {
    display: flex;
    flex-direction: column;
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.fog["30"]};
    border-radius: 8px;
    padding: 64px 56px;
  }

  .Portfolios__selectors {
    display: flex;
    flex-wrap: wrap;
    margin-left: -64px;
    margin-top: -16px;
  }

  .Portfolios__selector {
    margin-left: 64px;
    margin-top: 16px;
  }

  .Portfolios__tableContainer {
    width: 100%;
    display: flex;
    margin-top: 40px;
    padding: 40px 0;
    border-top: solid 1px ${({ theme }) => theme.colors.fog["30"]};
    border-bottom: solid 1px ${({ theme }) => theme.colors.fog["30"]};
  }

  .Portfolios__tableContainerTextContent {
    flex-shrink: 0;
    width: 320px;
  }

  .Portfolios__tableContainerTextTitle {
    margin-top: 4px;
  }

  .Portfolios__tableContainerTextDescription {
    margin-top: 16px;
  }

  .Portfolios__table {
    flex-grow: 1;
    margin-left: 60px;
  }

  .Portfolios__pageLink {
    margin-top: 40px;

    .Link__icon {
      width: 14px;
      height: 14px;
      margin-top: 0.1em;
    }
  }

  .Portfolios__loader {
    fill: ${({ theme }) => theme.colors.dusk["80"]};
    margin-top: 64px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Portfolios {
      padding-left: 32px;
      padding-right: 32px;
    }

    .Portfolios__tableContainer {
      flex-direction: column;
      padding: 40px 0;
    }

    .Portfolios__tableContainerTextContent {
      width: unset;
    }

    .Portfolios__table {
      margin-left: 0;
      margin-top: 60px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Portfolios {
      box-shadow: none;
      border-radius: 0;
      padding: 0;
    }

    .Portfolios__tableContainer {
      margin-top: 32px;
      padding: 32px 0;
    }

    .Portfolios__pageLink {
      margin-top: 32px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Portfolios__selectors {
      margin-left: 0;
      margin-top: -16px;
    }

    .Portfolios__selector {
      display: flex;
      justify-content: center;
      margin-left: 0;
      margin-top: 16px;
      width: 33.33%;
    }
  }
`;

gql`
  fragment PortfoliosContent on ComponentPortfoliosPortfolios {
    portfoliosDescription
    fundMenuLink
    fundMenuLabel
    defaultSlug
  }
`;

export { Portfolios };
export type { Props as PortfoliosProps };
